import { AfterViewInit, Component } from '@angular/core';
import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import { SafeResourceUrl } from '@angular/platform-browser';
import * as L from 'leaflet';
import { CadastreDataService } from '../services/cadastre-data.service';
import { GeometryService } from '../services/geometry.service';
import { PdfUrlService } from '../services/pdf-url.service';
import { CadastrePoint, EdgeSelection, Parcel, ParcelSelection } from '../models/cadastre.models';

@Component({
  selector: 'app-cadastre-map',
  standalone: true,
  imports: [NgIf, NgFor, DecimalPipe],
  templateUrl: './cadastre-map.component.html',
  styleUrl: './cadastre-map.component.scss'
})
export class CadastreMapComponent implements AfterViewInit {
  private map!: L.Map;
  private selectedEdgeLayers = new Map<string, L.Polyline>();
  private selectedParcelLayers = new Map<string, L.Polygon>();

  selectedEdges: EdgeSelection[] = [];
  selectedParcels: ParcelSelection[] = [];
  selectedPdfUrl?: SafeResourceUrl;
  selectedPdfTitle = '';

  constructor(
    readonly data: CadastreDataService,
    private readonly geometry: GeometryService,
    private readonly pdfUrl: PdfUrlService
  ) {}

  get totalSelectedLengthMeters(): number {
    return this.selectedEdges.reduce((sum, edge) => sum + edge.lengthMeters, 0);
  }

  get totalSelectedAreaSquareMeters(): number {
    return this.selectedParcels.reduce((sum, parcel) => sum + parcel.areaSquareMeters, 0);
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.drawParcels();
    this.drawColumns();
    this.drawPoints();
  }

  openPdf(parcel: Parcel): void {
    this.selectedPdfUrl = this.pdfUrl.toSafeUrl(parcel.pdfUrl);
    this.selectedPdfTitle = `Grundstück ${parcel.name} | EZ ${parcel.ez}`;
  }

  closePdf(): void {
    this.selectedPdfUrl = undefined;
    this.selectedPdfTitle = '';
  }

  private initMap(): void {
    this.map = L.map('map', { zoomControl: true });

    const osm = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 22,
      maxNativeZoom: 19,
      attribution: '&copy; OpenStreetMap contributors'
    });

    const wienKarte = L.tileLayer(
      'https://mapsneu.wien.gv.at/basemap/geolandbasemap/normal/google3857/{z}/{y}/{x}.png',
      {
        maxZoom: 22,
        maxNativeZoom: 19,
        attribution: 'basemap.at / Stadt Wien'
      }
    );

    const wienOrtho = L.tileLayer(
      'https://mapsneu.wien.gv.at/basemap/bmaporthofoto30cm/normal/google3857/{z}/{y}/{x}.jpeg',
      {
        maxZoom: 22,
        maxNativeZoom: 20,
        attribution: 'basemap.at / Stadt Wien'
      }
    );

    wienOrtho.addTo(this.map);

    L.control.layers(
      {
        OpenStreetMap: osm,
        'Wien Karte': wienKarte,
        'Wien Orthofoto': wienOrtho
      },
      undefined,
      { collapsed: false }
    ).addTo(this.map);

    const bounds = L.latLngBounds(this.data.points.map(p => this.geometry.pointToLatLng(p)));
    this.map.fitBounds(bounds.pad(0.25));
  }

  private drawParcels(): void {
    for (const parcel of this.data.parcels) {
      const points = parcel.pointIds.map(id => this.data.getPointById(id));
      const area = this.geometry.polygonAreaSquareMeters(points);

      const polygon = L.polygon(
        points.map(point => this.geometry.pointToLatLng(point)),
        this.defaultParcelStyle(parcel)
      ).addTo(this.map);

      polygon.bindTooltip(
        [
          `Grundstücksnummer: ${parcel.name}`,
          `Einlagezahl: ${parcel.ez}`,
          parcel.cadastreType
        ].join('\n'),
        {
          sticky: true,
          direction: 'right',
          offset: L.point(18, 0)
        }
      );

      polygon.on('click', event => {
        L.DomEvent.stopPropagation(event);
        this.toggleParcel(parcel, polygon, area);
        this.openPdf(parcel);
      });

      this.drawParcelEdges(parcel, points);
    }
  }

  private drawParcelEdges(parcel: Parcel, points: CadastrePoint[]): void {
    for (let i = 0; i < points.length; i++) {
      const from = points[i];
      const to = points[(i + 1) % points.length];
      const id = `${parcel.name}:${from.id}-${to.id}`;
      const length = this.geometry.distanceMeters(from, to);

      const line = L.polyline(
        [this.geometry.pointToLatLng(from), this.geometry.pointToLatLng(to)],
        {
          color: '#111827',
          weight: 5,
          opacity: 0.85
        }
      ).addTo(this.map);

      line.bindTooltip(`(${from.id}, ${to.id})`, {
        sticky: true,
        direction: 'right',
        offset: L.point(18, 0)
      });

      line.on('click', event => {
        L.DomEvent.stopPropagation(event);
        this.toggleEdge(id, parcel, from, to, length, line);
      });
    }
  }

  private drawPoints(): void {
    for (const point of this.data.points) {
      const marked = point.typ === 20;

      const tooltipLines = [
        `${point.id}`,
        `X: ${point.x}`,
        `Y: ${point.y}`
      ];

      if (marked) {
        tooltipLines.push('markiert');
      }

      L.circleMarker(this.geometry.pointToLatLng(point), {
        radius: marked ? 6 : 5,
        color: '#000000',
        weight: 2,
        fill: true,
        fillColor: marked ? '#000000' : '#ffffff',
        fillOpacity: marked ? 1 : 0
      })
        .bindTooltip(tooltipLines.join('\n'), {
          sticky: true,
          direction: 'right',
          offset: L.point(18, 0)
        })
        .addTo(this.map);
    }
  }

  private drawColumns(): void {
    for (const column of this.data.columns) {
      const from = this.data.getPointById(column.fromPointId);
      const to = this.data.getPointById(column.toPointId);

      L.polyline(
        [this.geometry.pointToLatLng(from), this.geometry.pointToLatLng(to)],
        {
          color: column.color,
          weight: 6,
          opacity: 0.95,
          dashArray: '6 4'
        }
      )
        .bindTooltip(column.name, {
          sticky: true,
          direction: 'right',
          offset: L.point(18, 0)
        })
        .addTo(this.map);
    }
  }

  private toggleEdge(
    id: string,
    parcel: Parcel,
    from: CadastrePoint,
    to: CadastrePoint,
    length: number,
    line: L.Polyline
  ): void {
    if (this.selectedEdgeLayers.has(id)) {
      this.selectedEdgeLayers.delete(id);
      this.selectedEdges = this.selectedEdges.filter(edge => edge.id !== id);
      line.setStyle({ color: '#111827', weight: 5, opacity: 0.85 });
      return;
    }

    this.selectedEdgeLayers.set(id, line);
    this.selectedEdges = [
      ...this.selectedEdges,
      {
        id,
        parcelName: parcel.name,
        fromPointId: from.id,
        toPointId: to.id,
        lengthMeters: length
      }
    ];

    line.setStyle({ color: '#ef4444', weight: 8, opacity: 1 });
  }

  private toggleParcel(parcel: Parcel, polygon: L.Polygon, area: number): void {
    if (this.selectedParcelLayers.has(parcel.name)) {
      this.selectedParcelLayers.delete(parcel.name);
      this.selectedParcels = this.selectedParcels.filter(item => item.id !== parcel.name);
      polygon.setStyle(this.defaultParcelStyle(parcel));
      return;
    }

    this.selectedParcelLayers.set(parcel.name, polygon);
    this.selectedParcels = [
      ...this.selectedParcels,
      {
        id: parcel.name,
        parcelName: parcel.name,
        areaSquareMeters: area
      }
    ];

    polygon.setStyle({
      color: '#ef4444',
      weight: 3,
      fill: true,
      fillColor: '#ef4444',
      fillOpacity: 0.28,
      opacity: 1
    });
  }

  private defaultParcelStyle(parcel: Parcel): L.PathOptions {
    return {
      color: parcel.strokeColor,
      weight: 2,
      fill: true,
      fillColor: parcel.strokeColor,
      fillOpacity: 0.08,
      opacity: 0.9
    };
  }
}
